import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageSquare, CheckCircle, Loader2, XCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const ConfirmSignup = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Extract tokens and error info from URL (check both query params and hash)
  const accessToken = searchParams.get('access_token') || new URLSearchParams(window.location.hash.substring(1)).get('access_token');
  const refreshToken = searchParams.get('refresh_token') || new URLSearchParams(window.location.hash.substring(1)).get('refresh_token');
  const type = searchParams.get('type') || new URLSearchParams(window.location.hash.substring(1)).get('type');
  const errorParam = searchParams.get('error') || new URLSearchParams(window.location.hash.substring(1)).get('error');
  const errorDescription = searchParams.get('error_description') || new URLSearchParams(window.location.hash.substring(1)).get('error_description');

  // Debug logging
  console.log('URL params:', { accessToken: !!accessToken, refreshToken: !!refreshToken, type, errorParam, errorDescription });

  useEffect(() => {
    const confirmSignup = async () => {
      // Check if there's an error in the URL first
      if (errorParam) {
        if (errorParam === 'access_denied' && errorDescription) {
          setError(decodeURIComponent(errorDescription.replace(/\+/g, ' ')));
        } else {
          setError('Email verification failed. The link may have expired.');
        }
        setIsLoading(false);
        return;
      }

      // Handle successful confirmation
      if (type === 'signup' && accessToken && refreshToken) {
        try {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            setError(error.message);
          } else {
            toast({
              title: "Account confirmed!",
              description: "Your account has been successfully verified.",
            });
            // Redirect immediately to dashboard to prevent re-render with empty params
            setTimeout(() => {
              navigate('/dashboard');
            }, 500);
            return;
          }
        } catch (err: any) {
          setError(err.message || 'Failed to confirm account');
        }
      } else if (!type && !accessToken && !refreshToken && !errorParam) {
        // If no parameters at all, likely accessed directly
        setError('This page is for email confirmation only. Please check your email for the confirmation link.');
      } else {
        setError('Invalid confirmation link. Please try signing up again.');
      }
      setIsLoading(false);
    };

    confirmSignup();
  }, [accessToken, refreshToken, type, errorParam, errorDescription, toast]);

  const handleContinue = () => {
    navigate('/dashboard');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center space-x-2 mb-8">
            <MessageSquare className="h-10 w-10 text-white" />
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-white">Bravax</span>
              <span className="text-sm text-white/70 -mt-1">Forms</span>
            </div>
          </div>

          <Card className="card-elegant animate-scale-in">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <Loader2 className="h-16 w-16 text-primary animate-spin" />
              </div>
              <CardTitle className="text-2xl font-bold">Confirming your account</CardTitle>
              <CardDescription>
                Please wait while we verify your email address
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center space-x-2 mb-8">
            <MessageSquare className="h-10 w-10 text-white" />
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-white">Bravax</span>
              <span className="text-sm text-white/70 -mt-1">Forms</span>
            </div>
          </div>

          <Card className="card-elegant animate-scale-in">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <XCircle className="h-16 w-16 text-destructive" />
              </div>
              <CardTitle className="text-2xl font-bold">Confirmation failed</CardTitle>
              <CardDescription>
                {error}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center space-y-4">
                <p className="text-sm text-muted-foreground">
                  The confirmation link may have expired or is invalid.
                </p>
                
                <div className="flex flex-col space-y-2">
                  <Button
                    onClick={() => navigate('/signup')}
                    className="w-full btn-hero"
                  >
                    Try signing up again
                  </Button>
                  
                  <Button
                    onClick={() => navigate('/login')}
                    variant="outline"
                    className="w-full"
                  >
                    Back to sign in
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center space-x-2 mb-8">
          <MessageSquare className="h-10 w-10 text-white" />
          <div className="flex flex-col">
            <span className="text-2xl font-bold text-white">Bravax</span>
            <span className="text-sm text-white/70 -mt-1">Forms</span>
          </div>
        </div>

        <Card className="card-elegant animate-scale-in">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-16 w-16 text-success" />
            </div>
            <CardTitle className="text-2xl font-bold">Welcome to Bravax Forms!</CardTitle>
            <CardDescription>
              Your account has been successfully confirmed. You can now start creating forms.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <Button
                onClick={handleContinue}
                className="w-full btn-hero"
              >
                Continue to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConfirmSignup;